import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Checkbox from '../../common/Checkbox';
import { ReactComponent as CheckboxSvg } from '../../../assets/checkbox.svg';

import {
  currencyFormat,
} from '../../../util';
import { removePowerupIdError } from '../../../actions';

import styles from './PowerUpCardTest.module.css';
import { HtmlContent } from '../../common';
import { WellnessCoverage } from './WellnessCoverage';
import { useProp103Features } from '../../../hooks';

export const COVERAGE_DEFAULTS = {
  first: 0,
  last: -1,
};

const PowerUpCardTest = ({
  bundleModifierAdd = () => { },
  bundleModifierCheckSelection = () => { },
  calculatePrice = () => { },
  coverageDefault = COVERAGE_DEFAULTS.first,
  error = false,
  handleAddPowerup,
  handleRemovePowerup,
  petQuoteSelected,
  petType,
  powerUpInfo,
  selected = null,
  title = '',
}) => {
  const { t } = useTranslation('quoting');
  const [isMounted, setMounted] = useState(false);
  const [localSelected, setLocalSelected] = useState(selected);
  const [coverageSelected, setCoverageSelected] = useState(null);
  const dispatch = useDispatch();
  const isProp103 = useProp103Features();

  const coverageOptions = useMemo(
    () => powerUpInfo.BundleInsuranceModifiersEB
      ?.filter((modifier) => modifier.IsVisible && modifier.IsActive),
    [powerUpInfo],
  );

  const isMultipleWellness = useMemo(
    () => coverageOptions?.length >= 2,
    [coverageOptions],
  );

  useEffect(() => {
    if (coverageOptions?.length && !coverageSelected) {
      setCoverageSelected(coverageOptions.at(coverageDefault)?.Id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageOptions, coverageSelected]);

  useEffect(() => {
    setLocalSelected(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petQuoteSelected, selected]);

  useEffect(() => {
    if (isMultipleWellness) {
      if (selected) {
        const modifierOpt = coverageOptions
          .find((bundleItem) => bundleItem.IsSelected);

        setCoverageSelected(modifierOpt.Id);
      } else {
        setCoverageSelected(coverageOptions?.at?.(coverageDefault)?.Id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petQuoteSelected]);

  useEffect(() => {
    if (!isMounted) {
      setMounted(true);
    }
  }, [isMounted]);

  const handleAddPowerupCallback = useCallback((event) => {
    if (!localSelected && handleAddPowerup) {
      handleAddPowerup(event);
    }

    if (error) {
      dispatch(removePowerupIdError(powerUpInfo.Id));
    }
  }, [dispatch, error, handleAddPowerup, localSelected, powerUpInfo.Id]);

  const handleRemovePowerupCallback = useCallback((event) => {
    if ((localSelected || localSelected === null) && handleRemovePowerup) {
      handleRemovePowerup(event);
    }
    if (error) {
      dispatch(removePowerupIdError(powerUpInfo.Id));
    }
  }, [dispatch, error, handleRemovePowerup, localSelected, powerUpInfo.Id]);

  const selectedBundle = useMemo(() => {
    if (powerUpInfo?.BundleInsuranceModifiersEB?.length
      && coverageSelected) {
      return powerUpInfo.BundleInsuranceModifiersEB
        .find((item) => item.Id === coverageSelected);
    }
    return null;
  }, [powerUpInfo, coverageSelected]);

  const bundleSelected = useMemo(
    () => powerUpInfo?.BundleInsuranceModifiersEB
      ?.find((modifier) => modifier.Id === coverageSelected),
    [coverageSelected, powerUpInfo],
  );

  const handleBundleSelectionChanged = useCallback(() => {
    bundleModifierAdd(
      {
        isSelected: !bundleModifierCheckSelection(coverageSelected),
        powerUp: bundleSelected,
      },
    )();
  }, [
    bundleModifierAdd,
    bundleModifierCheckSelection,
    bundleSelected,
    coverageSelected,
  ]);

  const handleOnChangePowerup = useCallback((event) => {
    if (localSelected) {
      handleRemovePowerupCallback(event);
    } else if (isMultipleWellness) {
      handleBundleSelectionChanged();
    } else {
      handleAddPowerupCallback(event);
    }
  }, [
    handleAddPowerupCallback,
    handleBundleSelectionChanged,
    handleRemovePowerupCallback,
    localSelected,
    isMultipleWellness,
  ]);

  useEffect(() => {
    if (localSelected && isMultipleWellness) {
      const modifierOpt = powerUpInfo.BundleInsuranceModifiersEB
        .find((bundleItem) => bundleItem.IsSelected);
      if (modifierOpt.Id !== coverageSelected) {
        handleBundleSelectionChanged();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageSelected]);

  const price = useMemo(() => {
    if (powerUpInfo?.BundleInsuranceModifiersEB?.length) {
      return powerUpInfo.BundleInsuranceModifiersEB
        .find((item) => item.Id === coverageSelected)?.FeeAmount;
    }
    return calculatePrice(powerUpInfo);
  }, [calculatePrice, coverageSelected, powerUpInfo]);

  const titleToDisplay = useMemo(() => {
    if (isProp103) {
      return `${selectedBundle?.TitleText || ''} ${title}`.toUpperCase();
    }
    return `${title} ${selectedBundle?.TitleText || ''}`.toUpperCase();
  }, [isProp103, selectedBundle, title]);

  const descriptionToDisplay = useMemo(() => {
    if (isProp103) {
      return selectedBundle?.InputText || powerUpInfo.InputText || '';
    }
    return powerUpInfo.InputText;
  }, [isProp103, powerUpInfo, selectedBundle]);

  return (
    <section
      aria-labelledby={`Powerup-card-title-${powerUpInfo.Id}`}
      className={localSelected ? styles.containerSelected : styles.container}
      data-error={error}
      id={`Powerup-card-section-${powerUpInfo.Id}`}
    >
      <div className={styles.bodyContent}>
        <div>
          <Checkbox
            checked={localSelected}
            className={styles.checkbox}
            id={`Powerup-card-title-${powerUpInfo.Id}`}
            onClick={handleOnChangePowerup}
          >
            <span>{titleToDisplay}</span>

            <span>
              {t('powerUpsSection.paymentCycle.monthlyPrice', {
                value: currencyFormat({ value: price }),
              })}
            </span>
          </Checkbox>
        </div>

        {localSelected && (
          <div className={styles.addedPill}>
            <CheckboxSvg className={styles.checkInPill} />

            <span>{t('powerUpsSection.added')}</span>
          </div>
        )}

        <div className={styles.content}>
          <HtmlContent
            classNameContainer={styles.text}
            content={descriptionToDisplay}
            elementId={`${powerUpInfo.Id}-description`}
            elementType="p"
            shouldRefresh
          />

          {coverageOptions?.length > 0
            && (
              <WellnessCoverage
                bundleSelected={bundleSelected}
                coverageSelected={coverageSelected}
                petType={petType}
                powerup={powerUpInfo}
                setCoverageSelected={setCoverageSelected}
              />
            )}
        </div>
      </div>
    </section>
  );
};

export { PowerUpCardTest };
